/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 21/11/21
 */
import React, {memo, useState, useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import StackFillIcon from 'remixicon-react/StackFillIcon';
import classNames from 'classnames';
import styles from './index.less';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import SectionBox from '@/modules/templates/components/TemplateSectionsDrawer/components/SectionBox';
import BookmarksPreview from '@/modules/templates/components/TemplatePreview/components/BookmarksPreview';
import {useInfinite} from '@/utils/createInfiniteHook';
import {CACHE_KEY_LIST_SECTIONS, TYPE_SECTION} from '@/modules/templates/contants/sections';
import { useInfiniteScroll } from '@dofleini/use-scroll';
import SectionsApiService from '@/modules/templates/services/SectionsApiService';
import {OperatorFilter, TermFilter} from '@dofleini/query-builder';
import PlayLineIcon from 'remixicon-react/PlayLineIcon';
import {useDetailsContext} from '@/contexts/DetailsContext';
import {CACHE_KEY_LIST_TEMPLATES} from '@/modules/templates/contants/templates';
import {useAuditData} from '@/modules/auditorAudits/hooks/useAuditData';
import useIndexDbDexie from '@/hooks/useIndexDbDexie';
import {useGetOneAudits} from '@/modules/audits/hooks/useAudits';

const SERVER_PAGE = 35;

const TemplateSections = ({entity}) => {
  const {t} = useTranslation('templates');
  const [visible, setVisible] = useState(false);
  const [dataIntro, setDataIntro] = useState([]);
  const [dataSections, setDataSections] = useState([]);
  const {push} = useHistory();
  const {closeDialog} = useDetailsContext(CACHE_KEY_LIST_TEMPLATES);
  const {setDataIntro: setDataIntroCtx, setDataSections: setDataSectionsCtx, setCurrentAudit} = useAuditData();
  const {data: audit} = useGetOneAudits({id: entity?.audit});
  const {db} = useIndexDbDexie();

  const showDrawer = useCallback(() => {
    setVisible(!visible);
  }, [visible]);

  const closeDrawer = useCallback(() => {
    setVisible(false);
  }, []);


  // const handleEditTemplate = useCallback(() => {
  //   push(`/templates/manage/${entity?._id}`);
  // }, [entity?._id, push]);

  const handleRunAudit = useCallback(() => {
    closeDialog();
    setCurrentAudit({audit: audit?.data, establishment: audit?.data?.establishment?.name, service: audit?.data?.service?.name, ...entity});
    setDataIntroCtx(dataIntro);
    setDataSectionsCtx(dataSections);
    const data = {
      _id: entity?._id,
      currentAudit: {audit: audit?.data, establishment: audit?.data?.establishment?.name, service: audit?.data?.service?.name, ...entity},
      dataIntro,
      dataSections
    };

    // Update data
    db.table('audits').get(entity?._id).then((res) => {
      if (!res?._id) {
        db?.audits?.add(data).then(() => {
          // retrieve all posts inside the database
          db?.audits?.toArray();
        });
      }
    });
    // Update current audit
    db?.currentAudit.clear(); // Clear data
    db.table('currentAudit').get(entity?._id).then((res) => {
      if (!res?._id) {
        db?.currentAudit?.add({_id: entity?._id}).then(() => {
          // retrieve all posts inside the database
          db?.currentAudit?.toArray();
        });
      }
    });

    push(`/auditor/audits/run/${entity?.audit || entity?._id}`);
  }, [audit?.data, closeDialog, dataIntro, dataSections, db, entity, push, setCurrentAudit, setDataIntroCtx, setDataSectionsCtx]);

  const filters = useMemo(() => new OperatorFilter({
    type: 'AND', filters: [
      new TermFilter({field: 'template', value: entity?._id})
    ]
  }), [entity?._id]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isSuccess,
    onLoadCompleted
  } = useInfinite({
    key: [CACHE_KEY_LIST_SECTIONS, filters],
    service: SectionsApiService.search,
    filters,
    sort: '_id',
    queryConfig: { size: Math.max(25, SERVER_PAGE) },
  });

  useInfiniteScroll({
    fromWindow: false,
    hasNext: hasNextPage && !(isFetching || isLoading),
    onLoadMore: fetchNextPage
  });

  useEffect(() => {
    if (isSuccess) {
      onLoadCompleted && onLoadCompleted(data);
      setDataIntro(data?.filter((x) => x.type === TYPE_SECTION.DATA));
      setDataSections(data?.filter((x) => x.type !== TYPE_SECTION.DATA));
    }
  }, [data, isSuccess, onLoadCompleted]);

  const onScroll = useCallback((event) => {
    const target = event.target;
    if (!(isLoading && isFetching) && target.scrollTop + target.offsetHeight >= (target.scrollHeight - 100) && hasNextPage) {
      fetchNextPage();
    }
  }, [isLoading, isFetching, hasNextPage, fetchNextPage]);


  const [activeSection, setActiveSection] = useState(data?.[0]);

  useEffect(() => {
    if (document.querySelector(`#section-${activeSection?._id}`)) {
      document.querySelector(`#section-${activeSection?._id}`).scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [activeSection?._id]);

  return (
    <div className="w-full">
      <div className="section-play-and-edit" onClick={closeDrawer}>
        <Button className="flex" type="primary" size="large" icon={<PlayLineIcon />} onClick={handleRunAudit}>
          <span className="ml-2">{entity?.status === 'running' ? t('continueAudit') : t('startAudit')}</span>
        </Button>
      </div>

      <div className={classNames(styles.wrapperSidebarBookMarkDrawer, 'w-full h-full')}>
        <div className="sidebar-drawer" onBlur={() => {
          if (visible) {
            closeDrawer();
          }
        }}>
          <div className={styles.bookMark}>
            <div className={styles.bookMarkBar} onClick={showDrawer}>
              {t('audits.navigation')}
              <StackFillIcon style={{transform: 'rotate(180deg)', margin: '10px 0 0 0', padding: '2px'}} />
            </div>
          </div>
          <div className={classNames(styles.sidebarBookMarkDrawer, visible ? styles.animateSectionsDrawer : styles.hiddenSectionsDrawer)} id="sidebarBookMarkDrawer">
            <BookmarksPreview
              currentTemplate={entity}
              data={data}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              onScroll={onScroll}
            />
          </div>
        </div>

        <div className={classNames('p-4 ml-10', styles.sectionsDrawerContent)} onClick={() => setVisible(false)}>
          {
            dataIntro?.map((item) => {
              return (
                <SectionBox key={item?._id} section={item} id={'section-'+item?._id}/>
              );
            })
          }
          {
            dataSections?.map((item) => {
              return (
                <SectionBox key={item?._id} section={item} id={'section-'+item?._id}/>
              );
            })
          }
        </div>
      </div>
    </div>
  );
};

TemplateSections.propTypes = {
  entity: PropTypes.object,
};

export default memo(TemplateSections);

/**
 * cellRender
 * */
/**
 * This is not a components,
 * it is a function to render the Code components in a table cell
 * @param {String} text this is the value of the dataIndex of the table
 * @param {Object} form
 * @param {Object} initialValues
 * */
export const renderTemplateSections = (text, form, initialValues) => {

  return (
    <TemplateSections entity={initialValues}/>
  );
};
